import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Tooltip from 'react-tt';

import {
    Button,
    GlobeIcon,
    LocationPinIcon,
    SwitcherooIcon,
    SearchInput,
    WDSInputDropdown,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { formatAddressValue, useDevice } from 'wanderu-utils';

import google from 'wanderu-assets/images/powered_by_google_on_white.png';
import google2x from 'wanderu-assets/images/powered_by_google_on_white_hdpi.png';

import styles from './RoundTripInputDropdown.scss';
import variables from 'wanderu-css/variables.scss';
const {
    tooltipZIndex,
    tooltipSpacing,
} = variables;

const renderTooltip = (isError, name, text) => {
    const zIndex = parseInt(tooltipZIndex, 10);
    const spacing = parseInt(tooltipSpacing, 10);

    return isError && (
        <div className={styles.tooltip}>
            <Tooltip
                id={`${name}-tooltip`}
                location="bottom"
                spacing={spacing || 10}
                zIndex={zIndex || 0}
                sticky
            >
                <div className={styles.tooltipContent}>
                    {text}
                </div>
            </Tooltip>
        </div>
    );
};

const GoogleAttribution = (
    <div className={styles.poweredByGoogle}>
        <img
            alt="Powered by Google"
            src={google}
            srcSet={`${google2x} 2x`}
        />
    </div>
);

export function RoundTripInputDropdown(props) {
    const {
        cellClass,
        destinationInput,
        destinationPrediction,
        equalInputs,
        missingDestination,
        missingOrigin,
        onDestinationChange,
        onDestinationInputChange,
        onFocus,
        onOriginChange,
        onOriginInputChange,
        onSwitch,
        originInput,
        originPrediction,
        rowClass,
        t,
    } = props;

    const device = useDevice();

    const InputComponent = device === 'Mobile'
        ? SearchInput
        : WDSInputDropdown;

    const inputProps = {
        backText: t('baseApp:go-back-link'),
        clearText: t('baseApp:clear-button'),
        footer: GoogleAttribution,
        Icon: LocationPinIcon,
        onFocus,
    };

    return (
        <div className={rowClass}>
            <div
                className={classNames(
                    cellClass,
                    styles.cell,
                )}
            >
                <InputComponent
                    inputValue={formatAddressValue(originInput)}
                    items={[{
                        attribution: GoogleAttribution, children: originPrediction, id: 'origin-prediction', type: 'section',
                    }]}
                    name="origin"
                    onInputChange={onOriginInputChange}
                    onSelectionChange={onOriginChange}
                    placeholder={t('origin placeholder')}
                    {...inputProps}
                />
                {renderTooltip(missingOrigin, 'origin', t('pickLocationTooltip'))}
            </div>
            <Button
                className={styles.switcherooContainer}
                data-testid="swap-button"
                onClick={onSwitch}
                unstyled
            >
                <SwitcherooIcon
                    alt="switcheroo"
                    className={styles.switcheroo}
                />
            </Button>
            <div
                className={classNames(
                    cellClass,
                    styles.cell,
                )}
            >
                <InputComponent
                    inputValue={formatAddressValue(destinationInput)}
                    items={[
                        {
                            attribution: GoogleAttribution, children: destinationPrediction, id: 'destination-prediction', type: 'section',
                        },
                        {
                            children: [{
                                Icon: GlobeIcon,
                                id: 'explore',
                                name: t('baseApp:explore-destinations'),
                            }],
                            id: 'links',
                            type: 'section',
                        },
                    ]}
                    name="destination"
                    onInputChange={onDestinationInputChange}
                    onSelectionChange={onDestinationChange}
                    placeholder={t('destination placeholder')}
                    {...inputProps}
                />
                {renderTooltip(missingDestination, 'destination', t('pickLocationTooltip'))}
                {renderTooltip(equalInputs, 'equal', t('pickDifferentLocationTooltip'))}
            </div>
        </div>
    );
}

RoundTripInputDropdown.propTypes = {
    cellClass: PropTypes.string,
    destinationInput: PropTypes.string,
    destinationPrediction: PropTypes.arrayOf(PropTypes.shape({
        placeId: PropTypes.string,
        text: PropTypes.string,
    })),
    equalInputs: PropTypes.bool,
    missingDestination: PropTypes.bool,
    missingOrigin: PropTypes.bool,
    onDestinationChange: PropTypes.func,
    onDestinationInputChange: PropTypes.func,
    onFocus: PropTypes.func,
    onOriginChange: PropTypes.func,
    onOriginInputChange: PropTypes.func,
    onSwitch: PropTypes.func,
    originInput: PropTypes.string,
    originPrediction: PropTypes.arrayOf(PropTypes.shape({
        placeId: PropTypes.string,
        text: PropTypes.string,
    })),
    rowClass: PropTypes.string,
    t: PropTypes.func,
};

export default localize(RoundTripInputDropdown, ['t'], 'widget');
